/**
 * TRACK ASSIGNMENT from the lead Assign popup.
 *
 * A role that holds a track at "Own" is a TRACK-MEMBER role: its people are
 * handed a lead's track (TrackUser), not the lead itself. Such a role usually
 * has "Inbound — receive leads" OFF, which used to grey it out in the popup
 * with no way forward. It is now pickable — as a track assignment — by admins
 * and by roles that hold MANAGE on that same track.
 *
 * Pure readers of `access_config`, mirroring the server
 * (`core/tracks/trackOwn.ts`, `scope/trackScope/trackReach.ts`).
 */

type TrackRow = { level?: unknown; view?: unknown; edit?: unknown };
type AccessConfigLike = {
  stages?: Array<{
    permissions?: { track_management?: { tracks?: Record<string, TrackRow> } };
  }>;
};

function collect(
  accessConfig: unknown,
  match: (row: TrackRow) => boolean
): number[] {
  const ids = new Set<number>();
  for (const stage of (accessConfig as AccessConfigLike)?.stages ?? []) {
    const tracks = stage?.permissions?.track_management?.tracks ?? {};
    for (const [k, row] of Object.entries(tracks)) {
      const id = Number(k);
      if (Number.isFinite(id) && match(row ?? {})) ids.add(id);
    }
  }
  return [...ids];
}

/** Tracks the role holds at "Own" in any stage. */
export const ownTrackIdsOf = (accessConfig: unknown): number[] =>
  collect(accessConfig, (r) => r.level === "own");

/** Tracks the role holds at "Manage" in any stage (explicit, or the legacy
 *  level-less allow+allow pair — the same two cases the server accepts). */
export const managedTrackIdsOf = (accessConfig: unknown): number[] =>
  collect(
    accessConfig,
    (r) =>
      r.level === "manage" ||
      (r.level == null && r.view === "allow" && r.edit === "allow")
  );

/** `access_config` of a roles-pipeline row. */
export function accessConfigOfRole(role: unknown): unknown {
  const perms = (role as { permissions?: unknown })?.permissions;
  const perm = Array.isArray(perms) ? perms[0] : perms;
  return (perm as { access_config?: unknown } | undefined)?.access_config;
}

/**
 * The tracks of `role` the VIEWER may hand out: the role's Own tracks,
 * narrowed to the ones the viewer manages (admins: all of them).
 */
export function assignableTrackIds(
  role: unknown,
  viewer: { bypass: boolean; accessConfig: unknown }
): number[] {
  const own = ownTrackIdsOf(accessConfigOfRole(role));
  if (viewer.bypass) return own;
  const managed = new Set(managedTrackIdsOf(viewer.accessConfig));
  return own.filter((id) => managed.has(id));
}
